import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Users, Plus, TrendingUp, UserCheck, Briefcase, Target, FileText, ArrowRight, ShieldAlert, BookOpen } from 'lucide-react'
import StatCard from '../components/Dashboard/StatCard'
import RecentTalents from '../components/Dashboard/RecentTalents'
import RecentJobApplications from '../components/Dashboard/RecentJobApplications'
import { talents, jobApplications } from '../services/apiService'
import { getTalentTypeLabel, TALENT_TYPES } from '../utils/talentTypeUtils'
import { useAuth } from '../hooks/useAuth'
import PageHeader from '../components/UI/PageHeader'

const Dashboard = () => {
  const { hasPermission } = useAuth()

  // Permission: статистика (квадратики с цифрами)
  const canViewTalentStats = useMemo(() => hasPermission('talent-service', 'talent', 'stats'), [hasPermission])
  const canViewJobAppStats = useMemo(() => hasPermission('talent-service', 'job_application', 'stats'), [hasPermission])

  // Permission: списки (recent talents / recent job applications)
  const canListTalents = useMemo(() => hasPermission('talent-service', 'talent', 'list'), [hasPermission])
  const canListJobApps = useMemo(() => hasPermission('talent-service', 'job_application', 'list'), [hasPermission])

  // Таб виден если есть доступ хотя бы к stats или list
  const canShowTalentsTab = canViewTalentStats || canListTalents
  const canShowJobAppsTab = canViewJobAppStats || canListJobApps

  const [activeTab, setActiveTab] = useState(() => {
    if (canShowTalentsTab) return 'talents'
    if (canShowJobAppsTab) return 'jobApplications'
    return 'talents'
  })
  const [stats, setStats] = useState(null)
  const [recentTalents, setRecentTalents] = useState([])
  const [jobAppStats, setJobAppStats] = useState(null)
  const [recentJobApps, setRecentJobApps] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!canShowTalentsTab && !canShowJobAppsTab) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        if (activeTab === 'talents' && canShowTalentsTab) {
          const promises = []

          // Загружаем статистику только если есть доступ
          if (canViewTalentStats) {
            promises.push(talents.getTalentsStats())
          } else {
            promises.push(Promise.resolve(null))
          }

          // Загружаем список только если есть доступ
          if (canListTalents) {
            promises.push(talents.getTalents({
              limit: 5,
              includes: 'contacts,professions,country,city,currentStatus'
            }))
          } else {
            promises.push(Promise.resolve(null))
          }

          const [statsResponse, talentsResponse] = await Promise.all(promises)

          if (statsResponse) {
            setStats(statsResponse.data || statsResponse)
          }
          if (talentsResponse) {
            setRecentTalents(talentsResponse.talents || talentsResponse.data || [])
          }
        } else if (activeTab === 'jobApplications' && canShowJobAppsTab) {
          const promises = []

          if (canViewJobAppStats) {
            promises.push(jobApplications.getJobApplicationStats())
          } else {
            promises.push(Promise.resolve(null))
          }

          if (canListJobApps) {
            promises.push(jobApplications.getJobApplications({ limit: 5 }))
          } else {
            promises.push(Promise.resolve(null))
          }

          const [statsResponse, jobAppsResponse] = await Promise.all(promises)

          if (statsResponse) {
            setJobAppStats(statsResponse.data || statsResponse)
          }
          if (jobAppsResponse) {
            setRecentJobApps(jobAppsResponse.data || [])
          }
        }
      } catch (error) {
        console.error('Dashboard: Error fetching data:', error)
        setError(error.message || 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [activeTab, canShowTalentsTab, canShowJobAppsTab, canViewTalentStats, canViewJobAppStats, canListTalents, canListJobApps])

  if (error) {
    return (
      <div className="dark:bg-dark-bg-primary">
        <PageHeader
          className="mb-8"
          title="Dashboard"
          description="Talent management system overview"
        />

        {/* Tabs */}
        {(canShowTalentsTab || canShowJobAppsTab) && (
          <div className="mb-8">
            <div className="min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain border-b border-light-border-primary dark:border-dark-border-primary">
              <nav className="-mb-px flex w-max min-w-full gap-8" aria-label="Dashboard sections">
                {canShowTalentsTab && (
                  <button
                    onClick={() => setActiveTab('talents')}
                    className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'talents'
                        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-secondary dark:hover:border-dark-border-secondary'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Talents
                    </div>
                  </button>
                )}
                {canShowJobAppsTab && (
                  <button
                    onClick={() => setActiveTab('jobApplications')}
                    className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'jobApplications'
                        ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-secondary dark:hover:border-dark-border-secondary'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Job Applications
                    </div>
                  </button>
                )}
              </nav>
            </div>
          </div>
        )}

        {/* Error Content */}
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">Failed to load data</div>
            <div className="text-light-text-secondary dark:text-dark-text-secondary mb-4">{error}</div>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                // Trigger useEffect to refetch by toggling activeTab
                const currentTab = activeTab;
                setActiveTab(null);
                setTimeout(() => setActiveTab(currentTab), 0);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="dark:bg-dark-bg-primary">
      <title>Dashboard | Talent Service</title>
      <PageHeader
        className="mb-8"
        title="Dashboard"
        description="Talent management system overview"
      />

      {/* Tabs - only show tabs user has access to */}
      {(canShowTalentsTab || canShowJobAppsTab) && (
        <div className="mb-8">
          <div className="min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain border-b border-light-border-primary dark:border-dark-border-primary">
            <nav className="-mb-px flex w-max min-w-full gap-8" aria-label="Dashboard sections">
              {canShowTalentsTab && (
                <button
                  onClick={() => setActiveTab('talents')}
                  className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'talents'
                      ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-secondary dark:hover:border-dark-border-secondary'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Talents
                  </div>
                </button>
              )}
              {canShowJobAppsTab && (
                <button
                  onClick={() => setActiveTab('jobApplications')}
                  className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'jobApplications'
                      ? 'border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-secondary dark:hover:border-dark-border-secondary'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Job Applications
                  </div>
                </button>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* No access message */}
      {!canShowTalentsTab && !canShowJobAppsTab && (
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-4">
              <ShieldAlert className="w-16 h-16 text-light-text-tertiary dark:text-dark-text-tertiary" />
            </div>
            <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
              No access to statistics
            </h2>
            <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">
              You do not have permission to view statistics. Please go to your{' '}
              <Link to="/my-profile" className="text-primary-600 dark:text-primary-400 hover:underline">profile</Link>
              {' '}or use the menu items available to you.
            </p>
          </div>
        </div>
      )}

      {/* Talents Tab Content */}
      {activeTab === 'talents' && canShowTalentsTab && (
        <>
          {/* Statistics - only if has stats permission */}
          {canViewTalentStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <StatCard
                title="Total Talents"
                value={stats?.total || 0}
                icon={Users}
                color="red"
                loading={loading}
              />
              <StatCard
                title={getTalentTypeLabel(TALENT_TYPES.CANDIDATE)}
                value={stats?.byType?.candidates || 0}
                icon={UserCheck}
                color="green"
                loading={loading}
              />
              <StatCard
                title={getTalentTypeLabel(TALENT_TYPES.TRAINEE)}
                value={stats?.byType?.trainees || 0}
                icon={BookOpen}
                color="yellow"
                loading={loading}
              />
              <StatCard
                title={getTalentTypeLabel(TALENT_TYPES.EMPLOYEE)}
                value={stats?.byType?.employees || 0}
                icon={Briefcase}
                color="blue"
                loading={loading}
              />
              <StatCard
                title={getTalentTypeLabel(TALENT_TYPES.PRESALE)}
                value={stats?.byType?.preSales || 0}
                icon={Target}
                color="purple"
                loading={loading}
              />
            </div>
          )}

          {/* Quick Actions & Recent Talents - only if has list permission */}
          {canListTalents && (
            <>
              <div className="mb-8">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Quick Actions</h2>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                  <Link
                    to="/talents/new"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 dark:bg-primary-650 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-750 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Talent
                  </Link>
                  <Link
                    to="/talents"
                    className="flex items-center gap-2 px-4 py-2 bg-light-bg-tertiary dark:bg-gray-700 text-light-text-primary dark:text-dark-text-primary border border-light-border-secondary dark:border-gray-600 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-gray-600 transition-colors"
                  >
                    <Users className="w-5 h-5" />
                    All Talents
                  </Link>
                </div>
              </div>

              <div className="mb-8">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Recent Talents</h2>
                  <Link
                    to="/talents"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                  >
                    View All
                  </Link>
                </div>
                <RecentTalents talents={recentTalents} loading={loading} />
              </div>
            </>
          )}
        </>
      )}

      {/* Job Applications Tab Content */}
      {activeTab === 'jobApplications' && canShowJobAppsTab && (
        <>
          {/* Statistics - only if has stats permission */}
          {canViewJobAppStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <StatCard
                title="Total Applications"
                value={jobAppStats?.total || 0}
                icon={FileText}
                color="blue"
                loading={loading}
              />
              <StatCard
                title="Converted to Talents"
                value={jobAppStats?.convertedToTalents || 0}
                icon={ArrowRight}
                color="green"
                loading={loading}
              />
            </div>
          )}

          {/* Quick Actions & Recent Job Applications - only if has list permission */}
          {canListJobApps && (
            <>
              <div className="mb-8">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Quick Actions</h2>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                  <Link
                    to="/job-applications/new"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 dark:bg-primary-650 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-750 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Job Application
                  </Link>
                  <Link
                    to="/job-applications"
                    className="flex items-center gap-2 px-4 py-2 bg-light-bg-tertiary dark:bg-gray-700 text-light-text-primary dark:text-dark-text-primary border border-light-border-secondary dark:border-gray-600 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-gray-600 transition-colors"
                  >
                    <FileText className="w-5 h-5" />
                    All Applications
                  </Link>
                </div>
              </div>

              <div className="mb-8">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Recent Job Applications</h2>
                  <Link
                    to="/job-applications"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                  >
                    View All
                  </Link>
                </div>
                <RecentJobApplications jobApplications={recentJobApps} loading={loading} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Dashboard
